import {
  FieldReadable,
  GroupReadable,
  ReportFilterReadable,
  ValuesReadable
} from '@crm/api'
import {
  CheckboxElement,
  DateElement,
  DateTimeElement,
  InputElement,
  OperatorElement,
  RadioElement,
  SelectElement,
  ValueElement
} from '@crm/components/DynamicFields'
import { ElementType, TaskFormElement } from '@crm/types'
import { Col, Form, Row } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import * as React from 'react'

type DynamicFormProps = {
  elements: TaskFormElement[]
  formName?: string
  initialValues?: AnyObject
  onSubmit?: (
    values:
      | FieldReadable
      | ReportFilterReadable
      | GroupReadable
      | ValuesReadable
  ) => void
}

const ElementComponents = Object.fromEntries([
  [ElementType.INPUT, InputElement],
  [ElementType.RADIO, RadioElement],
  [ElementType.CHECKBOX, CheckboxElement],
  [ElementType.DATE, DateElement],
  [ElementType.DATETIME, DateTimeElement],
  [ElementType.SELECT, SelectElement],
  [ElementType.OPERATOR, OperatorElement],
  [ElementType.VALUE, ValueElement]
])

export const DynamicForm = ({
  formName,
  elements,
  initialValues,
  onSubmit
}: DynamicFormProps) => {
  const [form] = Form.useForm()
  const [visibility, setVisibility] = React.useState({})
  const [values, setValues] = React.useState({})

  React.useEffect(() => {
    if (initialValues) {
      setValues(initialValues)
      form.setFieldsValue(initialValues)
    }
  }, [initialValues, form])

  React.useEffect(() => {
    const visibility_map = {} as Record<string, boolean>

    elements.forEach((element) => {
      let visible = true
      Object.keys(element.props?.rule || []).forEach((key) => {
        visible =
          visible && element.props.rule[key] === form.getFieldsValue()[key]
      })
      visibility_map[element.name] = visible
    })

    if (JSON.stringify(visibility_map) !== JSON.stringify(visibility)) {
      setVisibility(visibility_map)
    }
  }, [elements, form, visibility])

  const onValuesChange = (values: AnyObject): void => {
    if (values.field_name) {
      const label = elements
        .find(({ name }) => name === 'field_name')
        ?.props?.options?.find(
          ({ value }: { value: string }) => value === values.field_name
        )?.text

      values.type = elements
        ?.find(({ name }) => name === 'field_name')
        ?.props?.options?.find(
          ({ value }: { value: string }) => value === values.field_name
        )?.type

      if (label) {
        form.setFieldsValue({ label })
      }
    }

    setValues((v) => ({ ...v, ...values }))
  }

  return (
    <Form
      name={formName}
      form={form}
      onValuesChange={onValuesChange}
      onFinish={onSubmit}
      layout="vertical"
    >
      <Row gutter={12}>
        {elements?.length
          ? elements.map((element, key) => {
              if (visibility[element.name as keyof typeof visibility]) {
                const FormComponent = ElementComponents[element.type]

                return (
                  <Col
                    span={element?.props?.options?.col ?? 24}
                    key={`${element.name}-${key}`}
                  >
                    <FormComponent
                      element={element}
                      className={
                        ['filter_value'].includes(element.name) &&
                        'view-form__filter'
                      }
                      {...{
                        ...element.props,
                        ...(['filter_value', 'filter_operator'].includes(
                          element.name
                        ) && {
                          values
                        })
                      }}
                    />
                  </Col>
                )
              }
              return null
            })
          : null}
      </Row>
    </Form>
  )
}
